// jotai store for managing uploaded images
import { atom } from "jotai";
import { get as idbGet, set as idbSet } from "idb-keyval";

// Key used in IndexedDB to persist uploaded images
const PERSIST_KEY = "uploaded_images_persistance";

// Helper: Convert base64 (data URL) → File
export const base64ToFile = (base64DataUrl, name = "file", type = "") => {
  // Accepts data URL like `data:<mime>;base64,...` or raw base64 string
  const commaIndex = base64DataUrl.indexOf(",");
  const base64String =
    commaIndex >= 0 ? base64DataUrl.slice(commaIndex + 1) : base64DataUrl;
  const byteString = atob(base64String);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return new File([ab], name, { type });
};

// Helper: Convert File → base64 data URL (returns Promise)
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

// Helper: Load images array from IndexedDB and restore File + preview
export const loadImagesFromIDB = async () => {
  try {
    const saved = await idbGet(PERSIST_KEY);
    if (!saved || !Array.isArray(saved)) return [];
    // saved items are objects with metadata + base64
    return saved.map((img) => {
      if (!img.base64) {
        return { ...img, file: undefined, preview: img.preview || null };
      }
      try {
        const file = base64ToFile(
          img.base64,
          img.name || "file",
          img.type || ""
        );
        return { ...img, file, preview: URL.createObjectURL(file) };
      } catch (e) {
        return { ...img, file: undefined, preview: img.preview || null };
      }
    });
  } catch (e) {
    console.error("Failed to load uploaded images from IndexedDB:", e);
    return [];
  }
};

// Helper: Save images array to IndexedDB. Expects items to include either `base64` or `file`.
export const saveImagesToIDB = async (images) => {
  try {
    // Normalize: ensure each image has base64 stored
    const normalized = await Promise.all(
      images.map(async (img) => {
        // If already base64 persisted, keep it
        if (img.base64) {
          // store only metadata + base64 (avoid storing file or preview in DB object)
          const { base64, name, type, size, lastModified, id } = img;
          return { id, name, type, size, lastModified, base64 };
        }
        // If we have a File, convert it
        if (img.file instanceof File) {
          try {
            const base64 = await fileToBase64(img.file);
            return {
              id: img.id,
              name: img.file.name || img.name,
              type: img.file.type || img.type,
              size: img.file.size || img.size,
              lastModified: img.file.lastModified || img.lastModified,
              base64,
            };
          } catch (e) {
            console.warn(
              "Failed converting file to base64 for persistence:",
              e
            );
            return {
              id: img.id,
              name: img.name,
              type: img.type,
              size: img.size,
            };
          }
        }
        // Fallback: store whatever metadata exists
        const { id, name, type, size, lastModified } = img;
        return { id, name, type, size, lastModified };
      })
    );

    await idbSet(PERSIST_KEY, normalized);
  } catch (e) {
    console.error("Failed to save uploaded images to IndexedDB:", e);
  }
};

// Jotai internal atom holding images in-memory
const imagesAtomInternal = atom([]);

// Public atom: reads from internal atom; writes persist to IndexedDB
export const imagesAtom = atom(
  (get) => get(imagesAtomInternal),
  async (get, set, update) => {
    // Support functional updates (updater function) or direct value
    const prev = get(imagesAtomInternal);
    const next = typeof update === "function" ? update(prev) : update;
    set(imagesAtomInternal, next);
    // Persist next state to IDB (do not await to avoid blocking UI)
    try {
      await saveImagesToIDB(next || []);
    } catch (e) {
      console.error("Error persisting images to IDB:", e);
    }
  }
);

// When the atom is mounted, load persisted images from IDB and set atom state
imagesAtom.onMount = (setAtom) => {
  let cancelled = false;
  (async () => {
    const restored = await loadImagesFromIDB();
    if (!cancelled) {
      // Use the atom's setter to ensure any subscribers see the update
      setAtom(restored);
    }
  })();
  return () => {
    cancelled = true;
  };
};

// Export helpers for use elsewhere (tests or components)
export default imagesAtom;
